export const getTimeStatusWithStatus = (createdAt, deliveredAt, status) => {
  if (status !== "Delivered" && status !== "Order Cancelled") return null;
  if (!createdAt || !deliveredAt) return "N/A";

  const start = new Date(createdAt);
  const end = new Date(deliveredAt); // fixed time

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return "Invalid date";

  const diffInSec = Math.floor((end - start) / 1000);
  const hr = Math.floor(diffInSec / 3600);
  const min = Math.floor((diffInSec % 3600) / 60);
  const sec = diffInSec % 60;

  let result = "";
  if (hr > 0) result += `${hr} hr `;
  if (min > 0) result += `${min} min `;
  result += `${sec} sec`;
  return result.trim();
};
export const getTimeStatus = (createdAt) => {
  const createdTime = new Date(createdAt);
  const expiryTime = new Date(createdTime.getTime() + 30 * 60000);
  const now = new Date();

  if (now > expiryTime) return "Time Exceeded";

  const remainingMs = expiryTime - now;
  const minutes = Math.floor(remainingMs / 60000);
  const seconds = Math.floor((remainingMs % 60000) / 1000);
  return `${minutes}m ${seconds < 10 ? "0" + seconds : seconds}s remaining`;
};

// Lead time the backend's Agenda scheduled-dispatch job uses (must stay in
// sync with services/scheduled_dispatch_jobs.js → LEAD_MINUTES on the API).
export const SCHEDULED_DISPATCH_LEAD_MINUTES = 60;

const fmtTime = (d) =>
  d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
const fmtDateTime = (d) =>
  `${d.toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · ${fmtTime(d)}`;

/**
 * Per-delivery-type timing label. Plain-text only — pair with
 * getDeliveryTimingIcon(order) to render an icon next to the label.
 *
 *   Express ASAP     → 30-min countdown (or "Time Exceeded")
 *   Express + Slot   → "Scheduled · {label} · dispatch at {fireAt}"
 *                       or "Slot live · {label}" after Agenda job fires
 *   Standard         → "Standard · 48–72 hrs"
 *   Cancelled/done   → null (callers fall back to delivered/cancelled labels)
 */
export const getDeliveryTimingLabel = (order) => {
  if (!order) return null;
  const status = order.status;
  if (status === "Delivered" || status === "Order Cancelled") return null;

  const { deliveryType, scheduledSlot, createdAt } = order;

  if (deliveryType === "instant" && scheduledSlot?.start) {
    const slotStart = new Date(scheduledSlot.start);
    if (!Number.isNaN(slotStart.getTime())) {
      const fireAt = new Date(
        slotStart.getTime() - SCHEDULED_DISPATCH_LEAD_MINUTES * 60 * 1000
      );
      const slotLabel = scheduledSlot.label || fmtTime(slotStart);
      if (status === "scheduled") {
        return `Scheduled · ${slotLabel} · dispatch ${fmtDateTime(fireAt)}`;
      }
      return `Slot live · ${slotLabel}`;
    }
  }

  if (deliveryType === "standard") {
    return "Standard · 48–72 hrs";
  }

  return getTimeStatus(createdAt);
};

/**
 * Companion to getDeliveryTimingLabel — picks the MUI icon name + accent
 * colour the caller should pair with the label. Lets callers show a real
 * icon next to the timing text instead of an emoji prefix.
 */
export const getDeliveryTimingIcon = (order) => {
  if (!order) return null;
  const { deliveryType, scheduledSlot, status, createdAt } = order;
  if (status === "Delivered" || status === "Order Cancelled") return null;
  if (deliveryType === "instant" && scheduledSlot?.start) {
    if (status === "scheduled") {
      return { iconName: "Schedule", color: "#5B21B6", isLive: false };
    }
    return { iconName: "RocketLaunch", color: "#5B21B6", isLive: true };
  }
  if (deliveryType === "standard") {
    return { iconName: "LocalShipping", color: "#1E40AF", isLive: false };
  }
  if (createdAt) {
    const expiry = new Date(createdAt).getTime() + 30 * 60000;
    if (Date.now() > expiry) {
      return { iconName: "WarningAmber", color: "#B91C1C", isLive: false, isExceeded: true };
    }
  }
  return { iconName: "Bolt", color: "#B45309", isLive: true };
};

/**
 * Detailed scheduled-dispatch info for the order-detail page. Returns null
 * for non-scheduled orders. Useful for showing "When does Agenda fire?"
 * info to ops.
 */
export const getScheduledDispatchInfo = (order) => {
  if (!order) return null;
  const { deliveryType, scheduledSlot, status } = order;
  if (deliveryType !== "instant") return null;
  if (!scheduledSlot?.start) return null;
  const slotStart = new Date(scheduledSlot.start);
  const slotEnd = scheduledSlot.end ? new Date(scheduledSlot.end) : null;
  if (Number.isNaN(slotStart.getTime())) return null;
  const fireAt = new Date(
    slotStart.getTime() - SCHEDULED_DISPATCH_LEAD_MINUTES * 60 * 1000
  );
  const now = Date.now();
  return {
    slotLabel: scheduledSlot.label || null,
    slotDate: scheduledSlot.date || null,
    slotStart,
    slotEnd,
    fireAt,
    leadMinutes: SCHEDULED_DISPATCH_LEAD_MINUTES,
    fired: status !== "scheduled",
    cancelled: status === "Order Cancelled",
    timeUntilFireMs: fireAt.getTime() - now,
    fireAtPretty: fmtDateTime(fireAt),
    slotPretty: slotEnd
      ? `${fmtDateTime(slotStart)} – ${fmtTime(slotEnd)}`
      : fmtDateTime(slotStart),
  };
};